// Bootstrap StudyHub web SPA.
//
// Порядок:
//   1. enableMocking() — стартует MSW worker в dev (или при VITE_USE_MOCKS=true)
//   2. createRouter(routeTree) — TanStack Router
//   3. createRoot().render(AppProviders + RouterProvider)
//
// AppProviders собирает: QueryClient → trpc → Theme → Socket → I18n →
// Tooltip → GlobalHotkeys → Sonner. Полная композиция — providers/app-providers.tsx.

// ПЕРВЫМ: полифилы ES2024 (Map.groupBy / Object.groupBy) для старых браузеров/WebView.
import "./lib/polyfills";
// Сразу за ними: режим Zod без JIT. Zod пробует `new Function("")`, чтобы понять,
// доступен ли eval; под нашим CSP браузер репортит это как нарушение политики
// (красный Issue в DevTools у каждого пользователя), хотя исключение Zod ловит
// сам. Проба всё равно всегда возвращает false — `unsafe-eval` мы не открываем,
// — поэтому терять нечего: `jitless` пропускает пробу. Импорт до любых схем:
// признак кэшируется при первой валидации.
import "./lib/zod-jitless";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "./styles.css";
import { changesAppShell } from "./components/layout/_layout-state";
import { isFirstLevelPath } from "./components/navigation/nav-config";
import { RouteErrorComponent } from "./components/shared/route-error-component";
// Порядок значим: i18n поднимается до создания роутера, потому что язык
// публичной страницы берётся из её адреса (`URL_LOCALE`) — и роутеру он тоже
// нужен, чтобы возвращать префикс в генерируемые ссылки.
import { URL_LOCALE } from "./i18n";
import { hydrateAccessTokenFromNativeStorage } from "./lib/auth-storage";
import {
	captureAcquisitionTouch,
	captureDeepLinkTouch,
	captureInstallReferrer,
	initConsent,
	initMetaAppEvents,
	recordScreenView,
} from "./lib/ecosystem-analytics";
import { initAuthFetch } from "./lib/init-auth-fetch";
import { hydrateLastLoginMethodFromNativeStorage } from "./lib/last-login-method";
import {
	canonicalizeEnglishPrefix,
	createLocalePrefixRewrite,
	setAddressLocale,
} from "./lib/locale-url";
import { completeNativeAuthCallback } from "./lib/native-auth-callback";
import { createNavTransitionSeam, supportsViewTransitionTypes } from "./lib/nav-direction";
import { appComponentFrames } from "./lib/observability/component-stack";
import { GlobalErrorBoundary } from "./lib/observability/error-boundary";
import { captureClientError, initHawkClient } from "./lib/observability/hawk-client";
import { initPostHog } from "./lib/observability/posthog-client";
import {
	initPublicTrafficTracking,
	recordPublicPageView,
} from "./lib/observability/public-traffic";
import { initRuntimeSignals } from "./lib/observability/sw-error-bridge";
import { installIdbConnectionLossListener } from "./lib/powersync/idb-connection-loss";
import { queryClient } from "./lib/query-client";
import { scheduleDeferredPrecache } from "./lib/sw-precache-trigger";
import { enableMocking } from "./mocks/start";
import { AppProviders } from "./providers/app-providers";
import { routeTree } from "./routeTree.gen";
import { isRecordingInFlight, useRecorderStore } from "./stores/recorder";

// Потеря соединения с IndexedDB — РАНЬШЕ катчера Hawk: слушатели окна
// вызываются в порядке регистрации, и только первый успевает остановить сырые
// отказы VFS, о которых модуль сообщает одним структурным событием.
installIdbConnectionLossListener({
	isBusy: () => isRecordingInFlight(useRecorderStore.getState().status),
});
// Observability init ДО любого рендера — Hawk перехватывает early errors
// (window.onerror / unhandledrejection). Hawk — диагностика сбоев, а не
// аналитика, и согласия не ждёт; no-op без env-ключа. ТЗ §3.5 / §5.4.
initHawkClient();
// Единое согласие экосистемы IEC (OXEM-604): веб читает решение из общего
// cookie `iec_consent` и слушает баннер `consent.js` (его подключает
// `public/boot-consent.js`); приложение ждёт решения сервера, до него сбор
// Firebase выключен. Раньше PostHog: тот стартует только при согласии.
initConsent();
// Meta App Events SDK (OXEM-647) — только приложение: включается, когда
// реклама разрешена и человеку 18+, выключается при отзыве и выходе.
initMetaAppEvents();
// PostHog поднимается ТОЛЬКО при согласии на аналитику и гасится при отзыве
// (подписка на решение внутри). No-op без env-ключа.
initPostHog();
// Учёт посещаемости публичных страниц. Первую локацию модуль пропустит, если её
// уже записал серверный SEO-роут (маркер `sh-served-by` в документе), и посчитает
// сам, если маркера нет — под Capacitor документ приходит из локального бандла,
// и сервер такого захода не видит вовсе.
initPublicTrafficTracking();
// Ошибки Service Worker (офлайн-кэш, push) и крошки online/offline — они не
// видны катчеру сами по себе, см. sw-error-bridge.ts.
initRuntimeSignals();
// Источник входа (OXEM-400): UTM / referrer посадочной — в first-party cookie на
// 13 месяцев (first-party measurement, от согласия не зависит), в IEC ID уходит
// с началом входа. В приложении посадочной нет (документ
// из бандла) — там источники install referrer и deep link, см. mobile-shell ниже.
if (!__IS_CAPACITOR__) captureAcquisitionTouch();

// Bearer token → Authorization header на все API-запросы (Capacitor iOS ITP).
// ДО любых fetch (tRPC, Better Auth, PowerSync). Безвреден на web.
initAuthFetch();

// `/en/about` → `/about`. Английский живёт на голом пути (он же x-default),
// поэтому `/en/...` — второй адрес той же страницы, то есть дубль в индексе.
// Бутстрап ниже не прерываем: `location.replace` уже уводит документ, и
// начавшийся рендер всё равно будет отброшен вместе со страницей.
canonicalizeEnglishPrefix();

// Язык, выраженный адресом на старте. Дальше значение живое: подтвердится, что
// человек вошёл, — префикс уйдёт из адреса и из всех генерируемых ссылок
// (`components/i18n/locale-route-sync.tsx`), потому что языковой адрес есть
// только у гостя. Ставится ДО создания роутера: `output` rewrite'а читает его
// уже при построении первой ссылки.
setAddressLocale(URL_LOCALE);

/**
 * Сменился ли ПУТЬ в текущей навигации. Заполняется из события роутера ниже.
 *
 * Это единственный признак, по которому решается судьба прокрутки, и три
 * ожидания пользователя выражаются через него без исключений:
 *   переход по меню / ссылке в материал → путь другой → в начало страницы;
 *   кнопка «назад» браузера              → путь другой → на прежнее место;
 *   открыли-закрыли попап, сменили фильтр → путь тот же → не трогаем вовсе.
 *
 * Стартовое `true` — для самой первой отрисовки, до которой событий не было.
 */
let navigationChangedPath = true;

// Правило перехода связывается с реестром навигации ЗДЕСЬ: реестр знает, что
// считается пунктом меню, а правило (lib/nav-direction.ts) остаётся чистым.
const navTransition = createNavTransitionSeam(isFirstLevelPath, changesAppShell);

const router = createRouter({
	routeTree,
	// Языковой префикс публичных адресов (`/de/catalog/python-pro`) снимается ДО
	// сопоставления с деревом маршрутов и возвращается обратно во все
	// генерируемые ссылки. Благодаря этому дерево файлов остаётся одно, а
	// `<Link to="/catalog/$slug">` со страницы `/de/...` сам ведёт на
	// `/de/catalog/...` — язык не теряется при переходе. Разбор и сборка —
	// `lib/locale-url.ts`, там же объяснено, почему не `basepath` и не
	// зеркальное дерево `routes/$locale/*`.
	rewrite: createLocalePrefixRewrite(),
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
	// Переход по умолчанию — ради возврата по истории: «назад» не инициируется
	// ничьей ссылкой, поэтому был единственным переходом вообще без движения.
	// Форма ОБЪЕКТА выбрана не случайно: только она позволяет функции вернуть
	// `false` и тем отменить переход целиком (иначе смена фильтра платила бы
	// заморозкой отрисовки 50–90 мс без единого изменения на экране).
	// Условие поддержки обязательно: без `:active-view-transition-type` роутер
	// молча игнорирует типы и включает свой кроссфейд на КАЖДУЮ навигацию —
	// то есть ровно то, от чего мы уходим. Правило решения — lib/nav-direction.ts.
	defaultViewTransition: supportsViewTransitionTypes() ? { types: navTransition.types } : undefined,
	// Ошибка загрузчика/рендера любого маршрута остаётся ВНУТРИ layout'а, а не
	// подменяет собой всё приложение через GlobalErrorBoundary. Своего
	// `errorComponent` не было ни у одного роута — см. шапку компонента.
	defaultErrorComponent: RouteErrorComponent,
	// Отправка ошибки маршрута в Hawk — ЗДЕСЬ, а не в самом `RouteErrorComponent`.
	// Причина: `errorInfo.componentStack` доезжает только до `onCatch`. Роутер
	// собственному error-компоненту его не передаёт вовсе — в его пропсах
	// объявлено поле `info`, но `CatchBoundary` рисует компонент с `{error, reset}`
	// и ничего больше (проверено по сборке 1.170.27). Без стека компонентов
	// ошибки вида «Maximum update depth exceeded» неразбираемы: в трейсе видно
	// только внутренности React и radix, а имя нашего компонента — нигде.
	defaultOnCatch: (error, errorInfo) => {
		const { location, resolvedLocation, status, matches } = router.state;
		captureClientError(error, {
			source: "route-error-component",
			componentStack: errorInfo.componentStack ?? undefined,
			// Стек выше Hawk обрезает до трёх кадров — места падения. Наши
			// компоненты лежат глубже, поэтому едут отдельным коротким списком.
			appFrames: appComponentFrames(errorInfo.componentStack),
			// Последний матч, а НЕ место ошибки: упасть мог любой маршрут
			// цепочки или оверлей над ней. Цепочка целиком — в `matchIds`.
			routeId: matches.at(-1)?.routeId,
			matchIds: matches.map((m) => m.routeId),
			// Адрес и показанный матч расходятся, пока навигация в полёте:
			// `location` роутер ставит сразу, матчи — микротаской. Расхождение
			// в отчёте — признак петли навигации (OXEM-841: `/` под матчем
			// `/onboarding/`).
			pathname: location.pathname,
			resolvedPathname: resolvedLocation?.pathname,
			routerStatus: status,
		});
	},

	// ─────────────────────────────────────────────────────────────────────────
	// Прокрутка после навигации
	// ─────────────────────────────────────────────────────────────────────────
	// Раньше `setupScrollRestoration` подключался БЕЗУСЛОВНО (так устроен
	// router-core), но с выключенным восстановлением его обработчик умел ровно
	// одно — `window.scrollTo({top:0})` на КАЖДУЮ навигацию. Отсюда две жалобы
	// сразу: «назад» из материала открывал длинный список сверху, и смена
	// одного search-параметра (фильтр, открытый попап) тоже кидала вверх.
	//
	// Функция вместо `true` — не украшение: возврат `false` заставляет роутер не
	// трогать прокрутку ВООБЩЕ. Это и есть случай «мы никуда не уходили».
	// Заменить её на `true` — вернуть прыжок вверх на каждом фильтре.
	//
	// Ключ восстановления оставлен дефолтным (`location.state.__TSR_key`, то
	// есть ЗАПИСЬ ИСТОРИИ). Ключ по пути выглядел заманчиво, но давал неверное
	// поведение: клик по пункту меню возвращал бы туда, где ты в прошлый раз
	// остановился в этом разделе, а от явного перехода ждут начала списка.
	// С ключом по записи истории место помнит только та вкладка истории, куда
	// ведёт «назад», — как в браузере.
	scrollRestoration: () => navigationChangedPath,
});

// `pathChanged` считает сам роутер (`fromLocation?.pathname !== toLocation.pathname`)
// и кладёт в каждое событие навигации — берём готовое, а не пересчитываем.
// `onBeforeLoad` гарантированно проходит раньше `onRendered`, на котором роутер
// и решает, куда прокручивать, поэтому к моменту решения флаг уже свежий.
router.subscribe("onBeforeLoad", (event) => {
	navigationChangedPath = event.pathChanged;
	// Направление перехода — на <html> ДО того, как браузер снимет снимок экрана.
	// Правило целиком живёт в lib/nav-direction.ts, здесь только шов: это
	// единственное событие роутера, которое гарантированно проходит раньше
	// startViewTransition. Подробности и причина выбора именно атрибута — там же.
	navTransition.stamp(event);
});

// Учёт просмотра — на `onResolved`, а не на `onBeforeLoad`: считаем состоявшийся
// переход, а не намерение (загрузчик маршрута может упасть или увести редиректом).
// Адрес модуль берёт из `window.location` сам — у роутера он уже без языкового
// префикса, потому что тот снимается `createLocalePrefixRewrite` до сопоставления.
router.subscribe("onResolved", (event) => {
	recordPublicPageView();
	// Приложение: `screen_view` в Firebase по шаблону маршрута. На вебе no-op —
	// просмотры страниц там считает GTM.
	recordScreenView(router);

	// Возврат В ДРУГОЙ РАЗДЕЛ открывает его СВЕРХУ, а не там, где человек его
	// оставил. Правило владельца 2026-08-27: прокрутка помнится только при
	// углублении внутри одного раздела («Курсы» → курс → назад), а переход
	// «Курсы» → «Библиотека» → «Курсы» — это новый заход, и ждут от него начала
	// списка.
	//
	// Роутер сам так не умеет: его восстановление привязано к ЗАПИСИ ИСТОРИИ и
	// одинаково для любого «назад». Опция `scrollRestoration` тут не поможет —
	// она умеет только «восстановить» или «не трогать», а нам нужно «в начало».
	//
	// Момент выбран не случайно: между пунктами меню переход не запускается
	// вовсе (lib/nav-direction.ts возвращает `none`), поэтому прокрутка меняется
	// без снимка и без анимации — снаружи это просто открытый сверху раздел.
	const from = event.fromLocation;
	if (
		event.pathChanged &&
		from &&
		isFirstLevelPath(from.pathname) &&
		isFirstLevelPath(event.toLocation.pathname)
	) {
		window.scrollTo(0, 0);
	}
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

void enableMocking()
	.then(() =>
		// Мобильный cold start: WebView-хранилище могло быть очищено вместе с
		// данными приложения, а токен переживает это в Preferences. Гидратируем
		// ДО рендера, иначе первый auth.me уйдёт без Authorization и пользователь
		// увидит гостя поверх своей локальной библиотеки. На web — no-op.
		hydrateAccessTokenFromNativeStorage().catch(() => undefined),
	)
	.then(() =>
		// Тем же путём поднимаем подсказку «в прошлый раз входили через …»:
		// без гидратации зеркало в Preferences никто не читает и подсказка
		// пропадает после очистки данных WebView. На web — no-op.
		hydrateLastLoginMethodFromNativeStorage().catch(() => undefined),
	)
	.then(() => {
		const rootEl = document.getElementById("root");
		if (!rootEl) throw new Error("Root element not found");
		createRoot(rootEl).render(
			<StrictMode>
				<GlobalErrorBoundary>
					<AppProviders queryClient={queryClient}>
						<RouterProvider router={router} />
					</AppProviders>
				</GlobalErrorBoundary>
			</StrictMode>,
		);
	})
	.catch((err) => {
		// Без catch splash в #root остаётся навсегда (особенно заметно на mobile
		// cold start). Кнопка «Обновить» в index.html — запасной путь.
		console.error("[boot] failed before first paint:", err);
		const splash = document.getElementById("app-splash");
		splash?.classList.add("stalled");
		splash?.removeAttribute("aria-hidden");
	});

// Capacitor mobile-shell: регистрирует deep-link / app-state / keyboard
// listeners и связывает studyhub://auth/callback?code= → native-exchange →
// bearer (completeNativeAuthCallback). Под __IS_CAPACITOR__ guard — в web-сборке
// Vite define заменяет на false → ветка (и @studyhub/mobile с @capacitor/*)
// dead-code-eliminated. specifier через переменную — Vite не анализирует
// import статически (rules/capacitor-platform.md).
if (__IS_CAPACITOR__) {
	interface MobileShell {
		bootstrapMobileShell(deps: {
			navigate: (path: string) => void;
			completeOidcCallback?: (code: string, state: string) => Promise<void> | void;
			onDeepLink?: (rawUrl: string) => void;
		}): Promise<void>;
		notifyMobileShellRouterReady(): void;
	}
	// ЛИТЕРАЛЬНЫЙ specifier — @studyhub/mobile это workspace-пакет, который ОБЯЗАН
	// попасть в бандл. Переменная-specifier (как для @capacitor/*) заставляла Vite
	// НЕ бандлить пакет → в рантайме "Failed to resolve module specifier
	// '@studyhub/mobile'" → catch глушил → bootstrapMobileShell не запускался →
	// deep-link appUrlOpen-listener не регистрировался → SSO-возврат не
	// обрабатывался (бесконечный прелоадер). На web этот блок снимается DCE
	// (__IS_CAPACITOR__=false). Переменная-specifier — ТОЛЬКО для @capacitor/* /
	// @capgo/* (external native plugins), НЕ для @studyhub/* (rules/capacitor-platform.md).
	// @ts-expect-error — @studyhub/mobile резолвится ТОЛЬКО Vite-алиасом (resolve.alias,
	// mobile-app/mobile-dev mode → apps/mobile/src/main.ts); tsc алиас не видит (нет в
	// tsconfig paths) и не должен — пакет тянет @capacitor/* типы, не нужные web-tsc.
	void (import("@studyhub/mobile") as unknown as Promise<MobileShell>)
		.then(async (m) => {
			await m.bootstrapMobileShell({
				navigate: (path) => {
					void router.navigate({ to: path });
				},
				completeOidcCallback: (code) => completeNativeAuthCallback(code),
				onDeepLink: captureDeepLinkTouch,
			});
			void captureInstallReferrer();
			// Router смонтирован (createRoot().render выше синхронно) →
			// flush pendingUrl из cold-start deep-link.
			m.notifyMobileShellRouterReady();
		})
		.catch((err) => {
			// НЕ глушим молча — иначе отказ mobile-shell (deep-link/push/OTA) незаметен.
			console.error("[mobile-shell] bootstrap failed:", err);
		});
}

// Регистрация Service Worker (PWA) — только в production и НЕ внутри Capacitor WebView.
// Origin Capacitor `https://localhost` persist между переустановками APK, SW persist
// → старый cache блокирует обновления. Push в Capacitor — через native plugin (FCM).
if (
	typeof window !== "undefined" &&
	"serviceWorker" in navigator &&
	import.meta.env.PROD &&
	!__IS_CAPACITOR__
) {
	window.addEventListener("load", () => {
		void navigator.serviceWorker.register("/sw.js").catch(() => {
			// Регистрация не критична: PWA не активирована, основной flow работает.
		});
		// Lazy-чанки SW добирает не на install, а по нашему сигналу — когда
		// первичный синк уже не конкурирует с прекэшем за полосу.
		scheduleDeferredPrecache();
	});
}
